/**
 * subscriptionHelper.js - Frontend utilities para validar estado de suscripción
 * Espejo del helper del backend para mantener consistencia
 */

/**
 * Verifica si una suscripción está activa
 * @param {Object} user - Objeto del usuario con subscriptionStatus y subscriptionCurrentPeriodEnd
 * @returns {Boolean} true si la suscripción está activa y no ha expirado
 */
export const isSubscriptionActive = (user) => {
  if (!user) return false;

  // Validar estado de suscripción
  const validStatuses = ['active', 'trialing', 'canceled']; // canceled conserva acceso hasta fin de periodo (igual que el backend)
  if (!validStatuses.includes(user.subscriptionStatus)) {
    return false;
  }

  // Validar fecha de expiración
  if (!user.subscriptionCurrentPeriodEnd) {
    return false;
  }

  const expiryDate = new Date(user.subscriptionCurrentPeriodEnd);
  const now = new Date();

  return expiryDate > now;
};

/**
 * Calcula días hasta que la suscripción expire
 * @param {Date|String} expiryDate - Fecha de expiración
 * @returns {Number} Número de días (puede ser negativo si ya expiró)
 */
export const getDaysUntilExpiry = (expiryDate) => {
  if (!expiryDate) return -1;

  const expiry = new Date(expiryDate);
  const now = new Date();
  const diff = expiry.getTime() - now.getTime();
  
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

/**
 * Verifica si la suscripción está en la zona de alerta (≤3 días)
 * @param {Date|String} expiryDate - Fecha de expiración
 * @returns {Boolean} true si quedan ≤3 días
 */
export const isInAlertZone = (expiryDate) => {
  const daysRemaining = getDaysUntilExpiry(expiryDate);
  return daysRemaining >= 0 && daysRemaining <= 3;
};

/**
 * Verifica si una suscripción ha expirado
 * @param {Object} user - Objeto del usuario
 * @returns {Boolean} true si la suscripción ha expirado
 */
export const hasExpired = (user) => {
  if (!user || !user.subscriptionCurrentPeriodEnd) return false;
  
  const expiryDate = new Date(user.subscriptionCurrentPeriodEnd);
  const now = new Date();
  
  return expiryDate <= now;
};

/**
 * Obtiene el estado completo de la suscripción
 * @param {Object} user - Objeto del usuario
 * @returns {Object} Objeto con información de suscripción
 */
export const getSubscriptionStatus = (user) => {
  if (!user) {
    return {
      isActive: false,
      hasExpired: false,
      isInAlertZone: false,
      daysRemaining: -1,
      expiryDate: null
    };
  }

  const expiryDate = user.subscriptionCurrentPeriodEnd;
  const daysRemaining = getDaysUntilExpiry(expiryDate);
  const isExpired = hasExpired(user);
  const isActive = isSubscriptionActive(user);
  const inAlertZone = isInAlertZone(expiryDate);

  return {
    isActive,
    hasExpired: isExpired,
    isInAlertZone: inAlertZone,
    daysRemaining,
    expiryDate,
    status: user.subscriptionStatus
  };
};
